<template>
  <div class="py-4 container-fluid">
    <div class="mt-4 row">
      <div class="col-12">
        <div class="card">
          <div class="card-header border-bottom">
            <div class="user d-flex align-items-center">
              <div class="col-12">
                <h5 class="mb-0">Phonebots</h5>
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table id="datatable-search" class="table table-flush">
              <thead class="thead-light">
                <tr>
                  <th>#</th>
                  <th>Company</th>
                  <th>Phonebot</th>
                  <th>License/UserKey</th>
                  <th>Open AI Key</th>
                  <th>Verbindung</th>
                  <th>MCP</th>
                  <th>Created At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!vbots || vbots.length === 0">
                  <td colspan="9" class="text-center">No Phonebots found.</td>
                </tr>
                <tr v-else v-for="(vbot, index) in vbots" :key="vbot.id || index">
                  <td>{{ index + 1 }}</td>
                  <td>{{ vbot.customer_name || 'N/A' }}</td>
                  <td>{{ vbot.bezeichnung || 'N/A' }}</td>
                  <td>{{ vbot.license_Key || 'N/A' }}</td>
                  <td>{{ maskApiKey(vbot.apen_ai_key) }}</td>
                  <td>
                    <span class="badge" :class="vbot.connection.badgeClass" :title="vbot.connection.tooltip">
                      {{ vbot.connection.label }}
                    </span>
                    <span v-for="warning in vbot.connection.warnings" :key="warning" class="badge bg-danger ms-1" :title="vbot.connection.warningTooltip">
                      {{ warning }}
                    </span>
                    <template v-if="vbot.connection.detail"><br><small>{{ vbot.connection.detail }}</small></template>
                  </td>
                  <td>
                    <template v-if="vbot.mcp">
                      <span class="badge" :class="vbot.mcp.badgeClass" :title="vbot.mcp.tooltip">
                        {{ vbot.mcp.label }}
                      </span>
                      <span v-for="warning in vbot.mcp.warnings" :key="warning" class="badge bg-danger ms-1" :title="vbot.mcp.warningTooltip">
                        {{ warning }}
                      </span>
                      <template v-if="vbot.mcp.detail"><br><small>{{ vbot.mcp.detail }}</small></template>
                    </template>
                    <template v-else>-</template>
                  </td>
                  <td>{{ formatDate(vbot.created_at) }}</td>
                  <td>
                    <button class="btn btn-sm btn-info" @click="editItem(vbot.license_Key)" :title="'Edit Phonebot ' + vbot.bezeichnung">
                      <i class="fas fa-pen"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from 'jquery';
import 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';

export default {
  name: 'Phonebots',
  data() {
    return {
      vbots: [],
      dataTable: null,
      initializationTimer: null,
    };
  },

  async mounted() {
    await this.fetchVbots();
  },

  beforeUnmount() {
    this.cleanupDataTable();
  },

  watch: {
    // Watch vbots changes für sichere DataTable updates
    vbots: {
      handler() {
        if (this.vbots && this.vbots.length > 0) {
          this.safeInitializeDataTable();
        }
      },
      deep: true
    }
  },

  methods: {
    maskApiKey(text) {
      const safeText = text || '';
      return safeText.length > 10 ? safeText.substring(0, 20) + '...' : safeText;
    },

    // Spalte "Verbindung": Zustand aus dem letzten Lebenszeichen von AI-Phone (POST /api/v2/phonebot-heartbeat)
    getConnection(heartbeat) {
      if (!heartbeat) {
        return {
          label: 'keine Meldung',
          badgeClass: 'bg-secondary',
          detail: '',
          tooltip: 'AI-Phone hat noch kein Lebenszeichen gesendet (Version ohne Heartbeat oder Heartbeat nicht aktiviert).',
          warnings: [],
          warningTooltip: '',
        };
      }

      const since = this.formatDuration(heartbeat.status_since);
      const lastSeen = this.formatDuration(heartbeat.received_at);
      const states = {
        online: { label: 'angemeldet', detail: `zuletzt vor ${lastSeen}` },
        not_registered: { label: 'nicht angemeldet', detail: `seit ${since}` },
        not_running: { label: 'Telefonteil läuft nicht', detail: `seit ${since}` },
        offline: { label: 'ausgefallen', detail: `keine Meldung seit ${lastSeen}` },
      };
      const state = states[heartbeat.state] || { label: 'unbekannter Status', detail: `zuletzt vor ${lastSeen}` };

      // Rot bei Störung (ausgefallen oder über mehrere Meldungen nicht angemeldet), gelb bei einer einzelnen Abweichung
      let badgeClass = 'bg-warning text-dark';
      if (heartbeat.disturbed) {
        badgeClass = 'bg-danger';
      } else if (heartbeat.state === 'online') {
        badgeClass = 'bg-success';
      }

      // Derselbe License-Key läuft gleichzeitig mehrfach: auf mehreren Rechnern (z. B. ein alter PC läuft noch)
      // oder mit mehreren Versionen auf einem Rechner (z. B. nach einem Neustart). Das Backend liefert nur gleichzeitig laufende Installationen
      const instances = heartbeat.instances || [];
      const machineNames = [...new Set(instances.map((i) => i.name || 'unbekannt'))];
      const versionsPerMachine = machineNames.map((name) => new Set(instances.filter((i) => (i.name || 'unbekannt') === name).map((i) => i.version)).size);
      const maxVersions = Math.max(0, ...versionsPerMachine);
      const warnings = [];
      if (machineNames.length > 1) {
        warnings.push(`${machineNames.length} Rechner aktiv`);
      }
      if (maxVersions > 1) {
        warnings.push(`${maxVersions} Versionen aktiv`);
      }
      const warningTooltip = warnings.length
        ? ['Derselbe License-Key läuft gleichzeitig mehrfach:']
          .concat(instances.map((i) => `${i.name || 'unbekannt'}, Version ${i.version || '-'} (zuletzt vor ${this.formatDuration(i.last_seen)})`))
          .join('\n')
        : '';

      return { ...state, badgeClass, tooltip: this.getHeartbeatTooltip(heartbeat), warnings, warningTooltip };
    },

    // Details aus der letzten Meldung. Die Texte kommen von AI-Phone und dürfen in der DataTable nur escaped erscheinen
    getHeartbeatTooltip(heartbeat) {
      const p = heartbeat.payload || {};
      const started = p.uptimeSeconds != null
        ? this.formatDate(new Date(new Date(heartbeat.received_at).getTime() - p.uptimeSeconds * 1000))
        : '-';
      const lines = [
        `Letzte Meldung: ${this.formatDate(heartbeat.received_at)}`,
        `Rechner: ${p.machineName || '-'}${p.serviceName ? ` (${p.serviceName})` : ''}`,
        `AI-Phone-Version: ${heartbeat.app_version || '-'}`,
        `Dienst gestartet: ${started}`,
        `Letzte Anmeldung: ${this.formatDate(p.lastRegistrationAt)}`,
        `Aktive Gespräche: ${heartbeat.active_calls ?? '-'}`,
        `Fehler seit letzter Meldung: ${p.errorsSinceLastHeartbeat ?? '-'}`,
      ];
      if (p.lastError) {
        lines.push(`Letzter Fehler (${this.formatDate(p.lastErrorAt)}): ${p.lastError}`);
      }
      return lines.join('\n');
    },

    // Spalte "MCP": Zustand aus der letzten Meldung des MCP-Dienstes (POST /api/v2/mcp-heartbeat).
    // null = beim Bot ist kein MCP-Server eingetragen und es kam keine Meldung, die Spalte zeigt dann "-"
    getMcpStatus(vbot) {
      const heartbeat = vbot.mcp_heartbeat;
      if (!heartbeat) {
        const hasMcpServer = Array.isArray(vbot.mcp_servers) && vbot.mcp_servers.some((server) => server && server.url && server.url.trim() !== '');
        if (!hasMcpServer) {
          return null;
        }
        return {
          label: 'keine Meldung',
          badgeClass: 'bg-secondary',
          detail: '',
          tooltip: 'Der MCP-Dienst hat noch nichts gemeldet (Version ohne Meldung an das Backend oder kein License-Key in seiner config.json).',
          warnings: [],
          warningTooltip: '',
        };
      }

      const since = this.formatDuration(heartbeat.status_since);
      const lastSeen = this.formatDuration(heartbeat.received_at);
      const sources = (heartbeat.payload && heartbeat.payload.sources) || [];
      const states = {
        healthy: { label: 'läuft', detail: this.getMcpDataState(sources) || `zuletzt vor ${lastSeen}` },
        degraded: { label: 'eingeschränkt', detail: `seit ${since}` },
        starting: { label: 'startet', detail: `seit ${since}` },
        stopped: { label: 'gestoppt', detail: `seit ${since}` },
        failed: { label: 'Start gescheitert', detail: `vor ${since}` },
        no_source_ready: { label: 'keine Quelle bereit', detail: `zuletzt vor ${lastSeen}` },
        offline: { label: 'ausgefallen', detail: `keine Meldung seit ${lastSeen}` },
      };
      const state = states[heartbeat.state] || { label: 'unbekannter Status', detail: `zuletzt vor ${lastSeen}` };

      // Rot bei Störung (ausgefallen, Start gescheitert, keine Quelle bereit, zu lange gestoppt oder beim Starten), grün im Normalbetrieb,
      // sonst gelb (eingeschränkt, z. B. abgelehnter Import mit weiter gültigem Datenstand)
      let badgeClass = 'bg-warning text-dark';
      if (heartbeat.disturbed) {
        badgeClass = 'bg-danger';
      } else if (heartbeat.state === 'healthy') {
        badgeClass = 'bg-success';
      }

      // Derselbe License-Key wird gleichzeitig von mehreren MCP-Diensten gemeldet (anderer Rechner oder anderer Port)
      const instances = heartbeat.instances || [];
      const warnings = instances.length > 1 ? [`${instances.length} MCP-Instanzen aktiv`] : [];
      const warningTooltip = warnings.length
        ? ['Derselbe License-Key wird gleichzeitig von mehreren MCP-Diensten gemeldet:']
          .concat(instances.map((i) => `${i.name || 'unbekannt'}, Port ${i.port || '-'} (zuletzt vor ${this.formatDuration(i.last_seen)})`))
          .join('\n')
        : '';

      return { ...state, badgeClass, tooltip: this.getMcpTooltip(heartbeat), warnings, warningTooltip };
    },

    // Datenstand unter dem Badge: bei einer Quelle deren Importzeit, bei mehreren nur die Anzahl (Details im Tooltip)
    getMcpDataState(sources) {
      if (sources.length === 1 && sources[0].importedAt) {
        return `Datenstand ${this.formatDate(sources[0].importedAt)}`;
      }
      return sources.length > 1 ? `${sources.length} Quellen` : '';
    },

    // Details aus der letzten Meldung und die letzten Ereignisse. Die Texte kommen vom MCP-Dienst und dürfen in der DataTable nur escaped erscheinen
    getMcpTooltip(heartbeat) {
      const p = heartbeat.payload || {};
      const started = p.uptimeSeconds != null
        ? this.formatDate(new Date(new Date(heartbeat.received_at).getTime() - p.uptimeSeconds * 1000))
        : '-';
      const lines = [
        `Letzte Meldung: ${this.formatDate(heartbeat.received_at)}`,
        `Rechner: ${p.machineName || '-'}${p.serviceName ? ` (${p.serviceName})` : ''}${p.port ? `, Port ${p.port}` : ''}`,
        `MCP-Version: ${heartbeat.mcp_version || '-'}`,
        `Dienst gestartet: ${started}`,
      ];
      (p.degradedReasons || []).forEach((reason) => lines.push(`Einschränkung: ${reason}`));
      (p.sources || []).forEach((source) => {
        const name = source.file ? `${source.file} (${source.id || '-'})` : (source.id || '-');
        let ready = 'Bereitschaft unbekannt';
        if (source.ready === true) {
          ready = 'bereit';
        } else if (source.ready === false) {
          ready = 'nicht bereit';
        }
        lines.push(`Quelle ${name}: ${ready}, ${source.recordCount ?? '-'} Datensätze, Datenstand ${this.formatDate(source.importedAt)}`);
        if (source.lastError) {
          const error = [source.lastError.reason, source.lastError.message].filter(Boolean).join(': ');
          lines.push(`  Letzter Fehler (${this.formatDate(source.lastError.at)}): ${error || '-'}`);
        }
      });

      const events = heartbeat.events || [];
      if (events.length) {
        const levels = { info: 'Info', warning: 'Warnung', error: 'Fehler' };
        lines.push('Letzte Ereignisse:');
        events.forEach((event) => {
          const message = (event.message || '').length > 200 ? `${event.message.substring(0, 200)} …` : (event.message || '');
          const source = event.source_id ? ` (${event.source_id})` : '';
          lines.push(`  ${this.formatDate(event.occurred_at || event.received_at)} ${levels[event.level] || event.level} ${event.event}${source}${message ? `: ${message}` : ''}`);
        });
      }
      return lines.join('\n');
    },

    // Zeitspanne bis jetzt für "seit …" und "vor …", z. B. "3 Min.", "2 Std.", "4 Tagen"
    formatDuration(dateString) {
      const minutes = Math.floor((Date.now() - new Date(dateString).getTime()) / 60000);
      if (isNaN(minutes)) {
        return '-';
      }
      if (minutes < 1) {
        return 'weniger als 1 Min.';
      }
      if (minutes < 60) {
        return `${minutes} Min.`;
      }
      const hours = Math.floor(minutes / 60);
      return hours < 48 ? `${hours} Std.` : `${Math.floor(hours / 24)} Tagen`;
    },

    // Für die HTML-Strings der DataTable: Texte von AI-Phone und vom MCP-Dienst (Rechnername, letzter Fehler …) nie ungeschützt einsetzen
    escapeHtml(value) {
      const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
      return String(value ?? '').replace(/[&<>"']/g, (char) => entities[char]);
    },

    // DataTable-Zelle mit Text, den Kunden festlegen (Firmenname, Bezeichnung): DataTables setzt Zellinhalte als HTML,
    // deshalb für die Anzeige escapen. Suchen und Sortieren bekommen den Rohwert
    renderText(data, type) {
      if (type !== 'display') {
        return data ?? '';
      }
      return this.escapeHtml(data ?? 'N/A');
    },

    // DataTable-Zelle für "Verbindung" und "MCP": Badge, rote Hinweise und Zeile darunter, alles escaped
    renderStatusCell(data, type) {
      if (!data) {
        return '-';
      }
      if (type !== 'display') {
        return [data.label, ...data.warnings].join(' ');
      }
      const warnings = data.warnings
        .map((warning) => ` <span class="badge bg-danger ms-1" title="${this.escapeHtml(data.warningTooltip)}">${this.escapeHtml(warning)}</span>`)
        .join('');
      const detail = data.detail ? `<br><small>${this.escapeHtml(data.detail)}</small>` : '';
      return `<span class="badge ${data.badgeClass}" title="${this.escapeHtml(data.tooltip)}">${this.escapeHtml(data.label)}</span>${warnings}${detail}`;
    },

    async fetchVbots() {
      try {
        console.log('📡 Fetching phonebots...');
        
        const response = await this.$store.dispatch('vbot/getPhonebots');
        
        if (Array.isArray(response)) {
          // Spalten "Verbindung" und "MCP" einmal beim Laden berechnen (Vue-Tabelle und DataTable nutzen denselben Wert)
          this.vbots = response.map((vbot) => ({ ...vbot, connection: this.getConnection(vbot.heartbeat), mcp: this.getMcpStatus(vbot) }));
          console.log('✅ Successfully loaded', this.vbots.length, 'phonebots');
        } else {
          console.error('❌ Response is not an array:', response);
          this.vbots = [];
        }
        
      } catch (error) {
        console.error('❌ Error fetching phonebots:', error);
        this.vbots = [];
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }

      try {
        const formattedDate = date.toISOString().split('T')[0];
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${formattedDate}, ${hours}:${minutes}`;
      } catch (error) {
        return '-';
      }
    },

    editItem(license_Key) {
      this.$router.push({
        name: 'EditPhonebot',
        params: { license_Key: license_Key } 
      });
    },

    // ROBUSTE DataTable Initialisierung
    safeInitializeDataTable() {
      // Clear existing timer
      if (this.initializationTimer) {
        clearTimeout(this.initializationTimer);
      }

      // Warte auf DOM updates und dann initialisiere
      this.initializationTimer = setTimeout(() => {
        this.$nextTick(() => {
          this.initializeDataTable();
        });
      }, 100); // 100ms delay für sicherere DOM-State
    },

    // SAUBERE DataTable Bereinigung
    cleanupDataTable() {
      if (this.initializationTimer) {
        clearTimeout(this.initializationTimer);
        this.initializationTimer = null;
      }

      if (this.dataTable) {
        try {
          // Event handlers entfernen
          $('#datatable-search tbody').off('click', '.edit-btn');
          
          // DataTable sicher zerstören
          if ($.fn.DataTable.isDataTable('#datatable-search')) {
            this.dataTable.clear();
            this.dataTable.destroy(true); // true = remove from DOM completely
          }
          
          this.dataTable = null;
          console.log('🧹 DataTable cleaned up successfully');
        } catch (error) {
          console.warn('⚠️ Error cleaning up DataTable:', error);
          this.dataTable = null;
        }
      }
    },

    initializeDataTable() {
      // Prüfe ob DOM-Element verfügbar ist
      const tableElement = document.getElementById('datatable-search');
      if (!tableElement) {
        console.warn('⚠️ Table element not found, retrying...');
        setTimeout(() => this.safeInitializeDataTable(), 50);
        return;
      }

      // Prüfe ob Daten verfügbar sind
      if (!this.vbots || this.vbots.length === 0) {
        console.log('📊 No data available for DataTable yet');
        return;
      }

      try {
        console.log('🔧 Initializing DataTable with', this.vbots.length, 'items');
        
        // Komplett saubere Bereinigung vor Initialisierung
        this.cleanupDataTable();

        // Sicherheitscheck: Warte noch einen Tick
        this.$nextTick(() => {
          try {
            this.dataTable = $('#datatable-search').DataTable({
              destroy: true, // Automatische Bereinigung falls bereits existiert
              data: this.vbots,
              columns: [
                { 
                  data: null,
                  title: '#',
                  render: (data, type, row, meta) => meta.row + 1,
                  width: '50px',
                  orderable: false
                },
                {
                  data: 'customer_name',
                  title: 'Company',
                  render: (data, type) => this.renderText(data, type),
                  defaultContent: 'N/A'
                },
                {
                  data: 'bezeichnung',
                  title: 'Phonebot',
                  render: (data, type) => this.renderText(data, type),
                  defaultContent: 'N/A'
                },
                {
                  data: 'license_Key',
                  title: 'License/UserKey',
                  render: (data, type) => this.renderText(data, type),
                  defaultContent: 'N/A'
                },
                {
                  data: 'apen_ai_key',
                  title: 'Open AI Key',
                  render: (data, type) => this.renderText(this.maskApiKey(data), type),
                  defaultContent: 'N/A'
                },
                {
                  data: 'connection',
                  title: 'Verbindung',
                  // Tooltip und Texte stammen teils aus dem Lebenszeichen von AI-Phone, deshalb alles escaped einsetzen
                  render: (data, type) => this.renderStatusCell(data, type),
                  defaultContent: '-'
                },
                {
                  data: 'mcp',
                  title: 'MCP',
                  // Tooltip und Texte stammen teils aus der Meldung des MCP-Dienstes, deshalb ebenso escaped
                  render: (data, type) => this.renderStatusCell(data, type),
                  defaultContent: '-'
                },
                {
                  data: 'created_at',
                  title: 'Created At',
                  render: (data) => this.formatDate(data),
                  defaultContent: '-'
                },
                {
                  data: null,
                  title: 'Action',
                  render: (data) => {
                    const licenseKey = data.license_Key || data.id;

                    if (!licenseKey) {
                      console.error('❌ Kein license_Key für Phonebot:', data.bezeichnung);
                      return '<span class="text-danger">Kein Key</span>';
                    }

                    // Key und Bezeichnung escaped einsetzen, die Bezeichnung legt der Kunde fest
                    return `
                      <button class='btn btn-sm btn-info edit-btn' data-id='${this.escapeHtml(licenseKey)}' title='Edit ${this.escapeHtml(data.bezeichnung || 'Phonebot')}'>
                        <i class='fas fa-pen'></i>
                      </button>
                    `;
                  },
                  orderable: false,
                  width: '80px'
                },
              ],
              order: [[0, 'asc']],
              pageLength: 25,
              responsive: true,
              autoWidth: true, // Verhindere Layout-Probleme
              language: {
                emptyTable: "Keine Phonebots verfügbar",
                info: "Zeige _START_ bis _END_ von _TOTAL_ Einträgen",
                infoEmpty: "Keine Einträge verfügbar", 
                infoFiltered: "(gefiltert von _MAX_ Gesamteinträgen)",
                lengthMenu: "Zeige _MENU_ Einträge",
                search: "Suchen:",
                zeroRecords: "Keine passenden Einträge gefunden",
                paginate: {
                  first: "‹‹",
                  last: "››",
                  next: "›",
                  previous: "‹"
                }
              },
              drawCallback: () => {
                // Sichere Border-Anwendung
                this.$nextTick(() => {
                  this.applyCustomBorders();
                });
              }
            });

            // Event handlers sicher hinzufügen
            this.$nextTick(() => {
              $('#datatable-search tbody').off('click', '.edit-btn'); // Remove old handlers first
              $('#datatable-search tbody').on('click', '.edit-btn', (event) => {
                const id = $(event.currentTarget).data('id');
                this.editItem(id);
              });
            });

            this.applyCustomBorders();
            console.log('✅ DataTable initialized successfully');
            
          } catch (error) {
            console.error('❌ Error initializing DataTable:', error);
          }
        });

      } catch (error) {
        console.error('❌ Error in DataTable initialization:', error);
      }
    },

    applyCustomBorders() {
      try {
        $('#datatable-search th, #datatable-search td').css({
          'border': '1px solid #ddd',
          'padding': '10px',
        });

        $('#datatable-search td:first-child, #datatable-search th:first-child').css({
          'border-left': '2px solid #ddd',
        });

        $('#datatable-search th').css({
          'border-top': '2px solid #ddd',
        });
      } catch (error) {
        console.warn('⚠️ Error applying borders:', error);
      }
    },
  },
};
</script>

<style scoped>
#datatable-search {
  table-layout: fixed;
  width: 100%;
}

#datatable-search td button {
  margin-right: 5px;
}
</style>